<template>
  <v-dialog
    v-model="$store.state.tosScreen.show"
    persistent
    max-width="320"
  >
    <v-card>
      <v-card-title class="headline">
        {{ $t('auth.tosHeader') }}
      </v-card-title>
      <v-card-text ref="tosContent">
        <template v-if="shouldRenderDynamicTos">
          <template
            v-for="(checkbox, index) in tosCheckboxes"
          >
            <v-checkbox
              :key="`${index}_checkbox`"
              v-model="dynamicTosStatus[`${index}_checkbox`]"
              required
            >
              <template v-slot:label>
                <div
                  v-html="checkbox.description"
                />
              </template>
            </v-checkbox>
          </template>
        </template>
        <template v-else>
          <template v-if="!tosModalDoubleCheckbox">
            <v-checkbox v-model="tosAccepted" required>
              <template v-slot:label>
                <div
                  v-html="$t('auth.tosText', {
                    terms: terms,
                    privacyPolicy: privacyPolicy
                  })"
                />
              </template>
            </v-checkbox>
          </template>
          <template v-if="!!tosModalDoubleCheckbox">
            <v-checkbox v-model="privacyPolicyAccepted" required>
              <template v-slot:label>
                <div
                  v-html="$t('auth.privacyPolicyText', {
                    privacyPolicy: privacyPolicy
                  })"
                />
              </template>
            </v-checkbox>
            <v-checkbox v-model="termsAccepted" required>
              <template v-slot:label>
                <div
                  v-html="$t('auth.termsText', {
                    terms: terms
                  })"
                />
              </template>
            </v-checkbox>
          </template>
        </template>
        <v-alert
          dense
          type="warning"
          class="caption"
          v-if="!responseError"
        >
          {{ $t('auth.rejectTOSLogoutNotification') }}
        </v-alert>
        <v-alert
          dense
          type="error"
          class="caption"
          v-else
        >
          {{ $t('auth.tosAcceptCurrentVersionError') }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="reject">
          {{ $t('auth.disagree') }}
        </v-btn>
        <v-btn
          color="primary"
          text
          :disabled="!isAccepted || loading"
          :loading="loading"
          @click="accept"
        >
          {{ $t('auth.agree') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import {mapGetters, mapActions} from 'vuex'

export default {
  name: 'TOSScreen',
  data() {
    return {
      tosAccepted: false,
      privacyPolicyAccepted: false,
      termsAccepted: false,
      dynamicTosStatus: {},
      loading: false,
      responseError: false
    }
  },
  computed: {
    ...mapGetters([
      'appSettings',
      'authSettings',
      'tosScreenState'
    ]),
    tosCheckboxes() {
      return _.get(this.authSettings, 'tos_checkboxes', [])
    },
    tosModalDoubleCheckbox() {
      return _.get(
        this.authSettings, 'tos_modal_double_checkbox', false
      )
    },
    terms() {
      return _.get(
        this.authSettings,
        'terms_file',
        _.get(this.appSettings, 'terms_of_use_url')
      )
    },
    privacyPolicy() {
      return _.get(
        this.authSettings,
        'privacy_policy_file',
        _.get(this.appSettings, 'privacy_policy_url')
      )
    },
    shouldRenderDynamicTos() {
      return _.isArray(this.tosCheckboxes) &&
        !_.isEmpty(this.tosCheckboxes)
    },
    isAccepted() {
      if (this.shouldRenderDynamicTos) {
        return !Object.keys(this.dynamicTosStatus).some(
          (checkbox) => {
            return !this.dynamicTosStatus[checkbox]
          }
        )
      }

      // eslint-disable-next-line no-extra-boolean-cast
      return !!this.tosModalDoubleCheckbox
        ? this.privacyPolicyAccepted && this.termsAccepted
        : this.tosAccepted
    }
  },
  created() {
    this.initDynamicTosStatus()
  },
  mounted() {
    this.handleCheckboxesLinks()
  },
  methods: {
    ...mapActions([
      'tosScreen',
      'acceptCurrentTOS',
      'userSignOut'
    ]),
    initDynamicTosStatus() {
      if (!this.shouldRenderDynamicTos) {
        return
      }

      this.tosCheckboxes.forEach((checkbox, index) => {
        const description = _.get(checkbox, 'description', '')

        if (!description) {
          return
        }

        const key = `${index}_checkbox`
        this.$set(this.dynamicTosStatus, key, false)
      })
    },
    reject() {
      if (this.tosScreenState.rejectAction) {
        this.tosScreenState.rejectAction()
      } else {
        this.tosScreen({show: false})
        this.userSignOut()
      }
    },
    async accept() {
      if (!this.isAccepted) {
        return
      }

      this.loading = true

      if (this.tosScreenState.acceptAction) {
        await this.tosScreenState.acceptAction()
      } else {
        const res = await this.acceptCurrentTOS()

        if (!res) {
          setTimeout(() => {
            this.loading = false
            this.responseError = true
          }, 500)
        }
      }
    },
    // This method is necessary in order to be able to open links within checkboxes, otherwise they don't do anything because they are inside of an already interactive element (label)
    handleCheckboxesLinks() {
      const checkboxes = this.$refs.tosContent.querySelectorAll('.v-input--checkbox')

      if (checkboxes.length) {
        checkboxes.forEach((checkbox) => {
          const links = checkbox.querySelectorAll('a')

          if (links.length) {
            links.forEach((link) => {
              link.setAttribute('target', '_blank')
              link.onclick = (e) => {
                e.stopPropagation()
              }
            })
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-alert {
  margin-bottom: 0;

  ::v-deep .v-alert__wrapper i {
    align-self: center;
  }

  ::v-deep .v-alert__content {
    line-height: 1.2em;
  }
}

.v-input {
  margin-bottom: 1em;

  ::v-deep p {
    margin-bottom: 0;
  }

  ::v-deep .v-messages {
    display: none;
  }
}

.v-input ::v-deep .v-label {
  height: auto;
}
</style>
